import { useEffect, useMemo, useState } from 'react';
import { api, fetchCompetentPersonOptions } from '../lib/api.js';
import { Modal } from './Modal.js';
import { InlinePersonnelForm, Personnel } from './InlinePersonnelForm.js';
import { generateForm11ClientPDF } from '../lib/pdf-form11-client.js';

export interface RenewalItem {
  id: string;
  certificate_number: string;
  company_name: string;
  equipment_name: string;
  internal_id_number: string;
  examination_date: string;
  next_inspection_date: string;
  test_method?: string;
  status: 'COMPLIANT' | 'EXPIRING_SOON' | 'OVERDUE';
  days_until_due: number;
  type?: 'FORM_9' | 'FORM_10' | 'FORM_11' | 'TRAINING';
}

export const CONDITION_OPTIONS = ['Good', 'Not Good', 'Not Applicable'];

export interface StagedForm11Renewal {
  id: string; // old certificate ID
  certificate_number: string;
  company_name: string;
  equipment_name: string;
  internal_id_number: string;
  last_examination_date: string;
  next_due_date: string;
  status: string;

  // Editable renewal values
  examination_date: string;
  personnel_id: string;

  // Section 5 (Points 10 to 16)
  condition_vessel: string;
  condition_piping: string;
  fittings_pressure_gauges: string;
  fittings_safety_valve: string;
  fittings_stop_valve: string;
  fittings_reducing_valve: string;
  fittings_additional_safety_valve: string;
  fittings_other_devices: string;
  repairs_required: string;
  repairs_period: string;
  repairs_other_condition: string;
  reduced_working_pressure_pending_repairs: string;
  safe_working_pressure_subrule_8: string;
  other_observations: string;

  // Cached full cert data for preview
  fullCertData?: Record<string, unknown>;
}

export interface Form11StagedGroup {
  key: string;
  company_name: string;
  last_examination_date: string;
  items: StagedForm11Renewal[];
}

interface Form11BulkRenewModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  selectedItems?: RenewalItem[];
  allItems: RenewalItem[];
}

function nextInspDate11(examISO: string): string {
  if (!examISO) return '';
  const d = new Date(examISO + 'T00:00:00');
  d.setMonth(d.getMonth() + 6);
  d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
}

function toDDMMYYYY(iso: string): string {
  if (!iso) return '';
  try {
    const [y, m, d] = iso.split('-');
    return `${d?.padStart(2, '0')}-${m?.padStart(2, '0')}-${y}`;
  } catch {
    return iso;
  }
}

export function Form11BulkRenewModal({
  open,
  onClose,
  onSuccess,
  selectedItems,
  allItems,
}: Form11BulkRenewModalProps) {
  const [stagedRows, setStagedRows] = useState<StagedForm11Renewal[]>([]);
  const [companyFilter, setCompanyFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('OVERDUE');

  // Batch toolbar state
  const [batchDate, setBatchDate] = useState(new Date().toISOString().slice(0, 10));
  const [batchPersonnelId, setBatchPersonnelId] = useState('');

  // Inspectors list for dropdowns
  const [inspectors, setInspectors] = useState<Array<{ id: string; full_name: string }>>([]);
  const [showNewPersonnelModal, setShowNewPersonnelModal] = useState(false);

  // Row Details Editor Modal (for Points 13-16 editing)
  const [editingDetailsRow, setEditingDetailsRow] = useState<StagedForm11Renewal | null>(null);

  // Live PDF preview state
  const [previewRow, setPreviewRow] = useState<StagedForm11Renewal | null>(null);
  const [previewPdfUrl, setPreviewPdfUrl] = useState<string | null>(null);

  // Submission state
  const [submitting, setSubmitting] = useState(false);
  const [submitProgress, setSubmitProgress] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Load inspectors
  useEffect(() => {
    if (!open) return;
    fetchCompetentPersonOptions('')
      .then((opts) => setInspectors(opts.map((o) => ({ id: o.value, full_name: o.label }))))
      .catch(() => {});
  }, [open]);

  // Initialize staged rows when modal opens
  useEffect(() => {
    if (!open) return;
    const sourceItems = (selectedItems && selectedItems.length > 0)
      ? selectedItems.filter((i) => i.type === 'FORM_11')
      : allItems.filter((i) => i.type === 'FORM_11');

    const todayISO = new Date().toISOString().slice(0, 10);
    const initialRows: StagedForm11Renewal[] = sourceItems.map((item) => ({
      id: item.id,
      certificate_number: item.certificate_number,
      company_name: item.company_name,
      equipment_name: item.equipment_name,
      internal_id_number: item.internal_id_number,
      last_examination_date: item.examination_date,
      next_due_date: item.next_inspection_date,
      status: item.status,
      examination_date: todayISO,
      personnel_id: '',
      condition_vessel: 'Good',
      condition_piping: 'Good',
      fittings_pressure_gauges: 'Good',
      fittings_safety_valve: 'Good',
      fittings_stop_valve: 'Good',
      fittings_reducing_valve: 'Not Applicable',
      fittings_additional_safety_valve: 'Not Applicable',
      fittings_other_devices: 'Not Applicable',
      repairs_required: 'NIL',
      repairs_period: 'Not Applicable',
      repairs_other_condition: 'NIL',
      reduced_working_pressure_pending_repairs: 'Not Required',
      safe_working_pressure_subrule_8: 'Not Applicable',
      other_observations: 'Safety valve operation and setting to be checked regularly.',
    }));

    const hasOverdue = initialRows.some((r) => r.status === 'OVERDUE');
    const defaultStatus = (selectedItems && selectedItems.length > 0)
      ? 'ALL'
      : hasOverdue
        ? 'OVERDUE'
        : 'ALL';

    setStagedRows(initialRows);
    setErrorMessage('');
    setCompanyFilter('ALL');
    setStatusFilter(defaultStatus);
    setPreviewRow(null);
    setPreviewPdfUrl(null);
    setEditingDetailsRow(null);
  }, [open, selectedItems, allItems]);

  // Unique companies for filter
  const companyOptions = useMemo(() => {
    const names = Array.from(new Set(stagedRows.map((r) => r.company_name))).filter(Boolean);
    return names.sort();
  }, [stagedRows]);

  // Filtered rows
  const displayedRows = useMemo(() => {
    return stagedRows.filter((r) => {
      if (companyFilter !== 'ALL' && r.company_name !== companyFilter) return false;
      if (statusFilter !== 'ALL' && r.status !== statusFilter) return false;
      return true;
    });
  }, [stagedRows, companyFilter, statusFilter]);

  // Batch apply helpers
  const handleApplyBatchDate = () => {
    if (!batchDate) return;
    setStagedRows((prev) =>
      prev.map((row) =>
        displayedRows.some((d) => d.id === row.id)
          ? { ...row, examination_date: batchDate }
          : row
      )
    );
  };

  const handleApplyBatchInspector = () => {
    if (!batchPersonnelId) return;
    setStagedRows((prev) =>
      prev.map((row) =>
        displayedRows.some((d) => d.id === row.id)
          ? { ...row, personnel_id: batchPersonnelId }
          : row
      )
    );
  };

  const handleApplyBatchConditions = (val: string) => {
    setStagedRows((prev) =>
      prev.map((row) =>
        displayedRows.some((d) => d.id === row.id)
          ? {
              ...row,
              condition_vessel: val,
              condition_piping: val,
              fittings_pressure_gauges: val,
              fittings_safety_valve: val,
              fittings_stop_valve: val,
              fittings_reducing_valve: val === 'Good' ? 'Not Applicable' : val,
            }
          : row
      )
    );
  };

  const handleApplyBatchRepairsNIL = () => {
    setStagedRows((prev) =>
      prev.map((row) =>
        displayedRows.some((d) => d.id === row.id)
          ? {
              ...row,
              repairs_required: 'NIL',
              repairs_period: 'Not Applicable',
              repairs_other_condition: 'NIL',
              reduced_working_pressure_pending_repairs: 'Not Required',
              safe_working_pressure_subrule_8: 'Not Applicable',
              other_observations: 'Safety valve operation and setting to be checked regularly.',
            }
          : row
      )
    );
  };

  const updateRow = (id: string, partial: Partial<StagedForm11Renewal>) => {
    setStagedRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, ...partial } : r))
    );
  };

  const removeRow = (id: string) => {
    setStagedRows((prev) => prev.filter((r) => r.id !== id));
  };

  const stagedGroups = useMemo<Form11StagedGroup[]>(() => {
    const map = new Map<string, Form11StagedGroup>();
    for (const row of displayedRows) {
      const key = `${row.company_name}::${row.last_examination_date || 'Unknown'}`;
      if (!map.has(key)) {
        map.set(key, {
          key,
          company_name: row.company_name,
          last_examination_date: row.last_examination_date || 'Unknown',
          items: [],
        });
      }
      map.get(key)!.items.push(row);
    }
    return Array.from(map.values());
  }, [displayedRows]);

  const handleApplyGroupValues = (
    groupKey: string,
    values: {
      examination_date?: string;
      personnel_id?: string;
      conditionsGood?: boolean;
      repairsDefault?: boolean;
    }
  ) => {
    setStagedRows((prev) =>
      prev.map((row) => {
        const rowKey = `${row.company_name}::${row.last_examination_date || 'Unknown'}`;
        if (rowKey === groupKey) {
          return {
            ...row,
            ...(values.examination_date ? { examination_date: values.examination_date } : {}),
            ...(values.personnel_id ? { personnel_id: values.personnel_id } : {}),
            ...(values.conditionsGood
              ? {
                  condition_vessel: 'Good',
                  condition_piping: 'Good',
                  fittings_pressure_gauges: 'Good',
                  fittings_safety_valve: 'Good',
                  fittings_stop_valve: 'Good',
                  fittings_reducing_valve: 'Not Applicable',
                }
              : {}),
            ...(values.repairsDefault
              ? {
                  repairs_required: 'NIL',
                  repairs_period: 'Not Applicable',
                  repairs_other_condition: 'NIL',
                  reduced_working_pressure_pending_repairs: 'Not Required',
                  safe_working_pressure_subrule_8: 'Not Applicable',
                  other_observations: 'Safety valve operation and setting to be checked regularly.',
                }
              : {}),
          };
        }
        return row;
      })
    );
  };

  // Row Live PDF Preview
  const openRowPreview = async (row: StagedForm11Renewal) => {
    setPreviewRow(row);
    setPreviewPdfUrl(null);

    let cert = row.fullCertData;
    if (!cert) {
      try {
        const res = await api.form11.get(row.id);
        cert = ((res as any).data ?? res) as Record<string, unknown>;
        updateRow(row.id, { fullCertData: cert });
      } catch {
        cert = {};
      }
    }

    try {
      const examISO = row.examination_date || new Date().toISOString().slice(0, 10);
      const nextInsp = nextInspDate11(examISO);
      const examDateDD = toDDMMYYYY(examISO);
      const extFindings = `(a) Good on ${examDateDD}`;

      const pdfUrl = generateForm11ClientPDF({
        certificate_number: `${row.certificate_number} (Renewed)`,
        examination_date: examISO,
        next_inspection_date: nextInsp,
        company: {
          company_name: cert.company_name as string,
          occupier_name: cert.occupier_name as string,
          factory_address: cert.factory_address as string,
          license_number: cert.license_number as string,
          registration_number: cert.registration_number as string,
          nic_code: cert.nic_code as string,
        },
        vessel_name_description: cert.vessel_name_description as string,
        vessel_serial_no: cert.vessel_serial_no as string,
        vessel_capacity: cert.vessel_capacity as string,
        vessel_model_no: cert.vessel_model_no as string,
        vessel_location: cert.vessel_location as string,
        manufacturer_name_address: cert.manufacturer_name_address as string,
        nature_of_process: cert.nature_of_process as string,
        date_of_construction: cert.date_of_construction as string,
        thickness_shell: cert.thickness_shell as string,
        thickness_dish: cert.thickness_dish as string,
        thickness_top_dish: cert.thickness_top_dish as string,
        thickness_bottom_dish: cert.thickness_bottom_dish as string,
        first_taken_into_use_date: cert.first_taken_into_use_date as string,
        safe_working_pressure_recommended: cert.safe_working_pressure_recommended as string,
        last_external_exam_date: toDDMMYYYY(cert.examination_date as string),
        last_internal_exam_date: cert.last_internal_exam_date as string,
        last_hydraulic_exam_details: cert.last_hydraulic_exam_details as string,
        last_ultrasonic_exam_date: cert.last_ultrasonic_exam_date as string,
        lagging_removed_status: cert.lagging_removed_status as string,
        exam_external_findings: extFindings,
        exam_internal_findings: cert.exam_internal_findings as string,
        exam_hydraulic_findings: cert.exam_hydraulic_findings as string,
        exam_ultrasonic_findings: cert.exam_ultrasonic_findings as string,
        condition_vessel: row.condition_vessel,
        condition_piping: row.condition_piping,
        fittings_pressure_gauges: row.fittings_pressure_gauges,
        fittings_safety_valve: row.fittings_safety_valve,
        fittings_stop_valve: row.fittings_stop_valve,
        fittings_reducing_valve: row.fittings_reducing_valve,
        fittings_additional_safety_valve: row.fittings_additional_safety_valve,
        fittings_other_devices: row.fittings_other_devices,
        safe_working_pressure_recommended_after: cert.safe_working_pressure_recommended_after as string,
        repairs_required: row.repairs_required,
        repairs_period: row.repairs_period,
        repairs_other_condition: row.repairs_other_condition,
        reduced_working_pressure_pending_repairs: row.reduced_working_pressure_pending_repairs,
        safe_working_pressure_subrule_8: row.safe_working_pressure_subrule_8,
        other_observations: row.other_observations,
      });

      setPreviewPdfUrl(pdfUrl);
    } catch {
      setPreviewPdfUrl(null);
    }
  };

  // Validation
  const validRows = useMemo(() => {
    return displayedRows.filter((r) => r.personnel_id && r.examination_date);
  }, [displayedRows]);

  const isValidAll = displayedRows.length > 0 && validRows.length === displayedRows.length;

  const handleBulkSubmit = async () => {
    if (!isValidAll) {
      setErrorMessage(`Please select a Competent Person and Examination Date for all ${displayedRows.length} certificates.`);
      return;
    }

    setSubmitting(true);
    setErrorMessage('');
    setSubmitProgress(`Renewing ${displayedRows.length} Form 11 certificates…`);

    try {
      const items = displayedRows.map((r) => ({
        old_certificate_id: r.id,
        examination_date: r.examination_date,
        personnel_id: r.personnel_id,
        condition_vessel: r.condition_vessel,
        condition_piping: r.condition_piping,
        fittings_pressure_gauges: r.fittings_pressure_gauges,
        fittings_safety_valve: r.fittings_safety_valve,
        fittings_stop_valve: r.fittings_stop_valve,
        fittings_reducing_valve: r.fittings_reducing_valve,
        fittings_additional_safety_valve: r.fittings_additional_safety_valve,
        fittings_other_devices: r.fittings_other_devices,
        repairs_required: r.repairs_required,
        repairs_period: r.repairs_period,
        repairs_other_condition: r.repairs_other_condition,
        reduced_working_pressure_pending_repairs: r.reduced_working_pressure_pending_repairs,
        safe_working_pressure_subrule_8: r.safe_working_pressure_subrule_8,
        other_observations: r.other_observations,
      }));

      await api.dashboard.bulkRenew({
        type: 'FORM_11',
        items,
      });

      onSuccess();
      onClose();
    } catch (err: unknown) {
      setErrorMessage(err instanceof Error ? err.message : 'Bulk renewal failed');
    } finally {
      setSubmitting(false);
      setSubmitProgress('');
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="⚡ Bulk Renew Form 11 Certificates (Pressure Vessels & Plants)" size="full">
      <div className="flex flex-col h-[calc(100vh-65px)] overflow-hidden">
        {/* Top Info & Filters */}
        <div className="p-3 bg-slate-50 border-b border-slate-200 flex flex-wrap items-center justify-between gap-3 shrink-0">
          <div className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-lg bg-purple-100 text-purple-700 font-bold flex items-center justify-center text-sm shadow-sm">
              🛢️
            </span>
            <div>
              <span className="font-bold text-slate-900 text-sm">
                Staging <span className="text-purple-700">{displayedRows.length}</span> Form 11 Certificates across{' '}
                <span className="text-purple-700">{stagedGroups.length}</span> Inspection Group(s)
              </span>
              <p className="text-xs text-slate-500">
                Organized by company inspection batches. Apply parameters per group or adjust individually.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Filter by company */}
            <select
              value={companyFilter}
              onChange={(e) => setCompanyFilter(e.target.value)}
              className="px-2.5 py-1.5 bg-white border border-slate-300 rounded-lg text-xs font-medium text-slate-700 shadow-sm"
            >
              <option value="ALL">All Companies ({companyOptions.length})</option>
              {companyOptions.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>

            {/* Filter by status */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-2.5 py-1.5 bg-white border border-slate-300 rounded-lg text-xs font-medium text-slate-700 shadow-sm"
            >
              <option value="ALL">All Statuses</option>
              <option value="OVERDUE">Overdue Only</option>
              <option value="EXPIRING_SOON">Expiring Soon</option>
              <option value="COMPLIANT">Compliant</option>
            </select>
          </div>
        </div>

        {/* ⚡ Batch Apply Toolbar */}
        <div className="p-3 bg-purple-50/60 border-b border-purple-200/80 shrink-0 shadow-sm">
          <div className="flex flex-wrap items-center gap-3 text-xs">
            <span className="font-bold text-purple-900 shrink-0 flex items-center gap-1.5">
              <span className="text-amber-500 text-sm">⚡</span>
              <span>Batch Apply All:</span>
            </span>

            {/* Batch Date */}
            <div className="flex items-center gap-1.5 bg-white p-1 rounded-lg border border-purple-200">
              <span className="text-slate-500 pl-1 font-medium">Date:</span>
              <input
                type="date"
                value={batchDate}
                onChange={(e) => setBatchDate(e.target.value)}
                className="px-2 py-1 text-xs border border-slate-200 rounded font-medium"
              />
              <button
                type="button"
                onClick={handleApplyBatchDate}
                className="px-2.5 py-1 bg-purple-700 hover:bg-purple-800 text-white font-semibold rounded text-xs transition-colors"
              >
                Apply
              </button>
            </div>

            {/* Batch Inspector */}
            <div className="flex items-center gap-1.5 bg-white p-1 rounded-lg border border-purple-200 min-w-[260px]">
              <span className="text-slate-500 pl-1 font-medium">Inspector:</span>
              <select
                value={batchPersonnelId}
                onChange={(e) => setBatchPersonnelId(e.target.value)}
                className="flex-1 px-2 py-1 text-xs border border-slate-200 rounded font-medium bg-white"
              >
                <option value="">-- Choose Inspector --</option>
                {inspectors.map((ins) => (
                  <option key={ins.id} value={ins.id}>{ins.full_name}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={handleApplyBatchInspector}
                disabled={!batchPersonnelId}
                className="px-2.5 py-1 bg-purple-700 hover:bg-purple-800 disabled:opacity-40 text-white font-semibold rounded text-xs transition-colors whitespace-nowrap"
              >
                Apply
              </button>
              <button
                type="button"
                onClick={() => setShowNewPersonnelModal(true)}
                title="Add New Personnel"
                className="px-2 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded text-xs transition-colors"
              >
                +
              </button>
            </div>

            {/* Quick Preset: Fittings to Good */}
            <button
              type="button"
              onClick={() => handleApplyBatchConditions('Good')}
              className="px-3 py-1.5 bg-white border border-purple-300 hover:bg-purple-100 text-purple-800 font-semibold rounded-lg text-xs transition-colors shadow-sm inline-flex items-center gap-1.5"
            >
              <span>✅</span>
              <span>Set All Fittings & Vessels to "Good"</span>
            </button>

            {/* Quick Preset: NIL Repairs */}
            <button
              type="button"
              onClick={handleApplyBatchRepairsNIL}
              className="px-3 py-1.5 bg-white border border-purple-300 hover:bg-purple-100 text-purple-800 font-semibold rounded-lg text-xs transition-colors shadow-sm inline-flex items-center gap-1.5"
            >
              <span>🛡️</span>
              <span>Reset Points 13–16 to Standard Defaults</span>
            </button>
          </div>
        </div>

        {errorMessage && (
          <div className="m-3 p-3 bg-red-50 border border-red-200 rounded-lg text-xs font-medium text-red-700 flex items-center justify-between">
            <span>{errorMessage}</span>
            <button type="button" onClick={() => setErrorMessage('')} className="text-red-500 font-bold">✕</button>
          </div>
        )}

        {/* Grouped Interactive Staging Area */}
        <div className="flex-1 min-h-0 overflow-y-auto p-3 space-y-4 bg-slate-50/50">
          {stagedGroups.length === 0 ? (
            <div className="card p-12 text-center text-slate-500 font-medium">
              No Form 11 certificates staged for renewal.
            </div>
          ) : (
            stagedGroups.map((group) => (
              <Form11GroupCard
                key={group.key}
                group={group}
                inspectors={inspectors}
                onApplyGroup={handleApplyGroupValues}
                onUpdateRow={updateRow}
                onRemoveRow={removeRow}
                onOpenRowPreview={openRowPreview}
                onEditDetails={(row) => setEditingDetailsRow(row)}
                onOpenNewPersonnel={() => setShowNewPersonnelModal(true)}
              />
            ))
          )}
        </div>

        {/* Bottom Actions Bar */}
        <div className="p-4 bg-slate-50 border-t border-slate-200 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <span className="text-xs font-semibold text-slate-700">
              Ready: <strong className="text-emerald-700">{validRows.length}</strong> / {displayedRows.length} certificates
            </span>
            {!isValidAll && (
              <span className="text-xs font-medium text-amber-700 bg-amber-100 px-2 py-0.5 rounded">
                ⚠️ {displayedRows.length - validRows.length} row(s) missing Competent Person
              </span>
            )}
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary text-sm h-10 px-5"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleBulkSubmit}
              disabled={submitting || !isValidAll}
              className="btn btn-primary text-sm h-10 px-6 font-semibold inline-flex items-center gap-2 shadow-sm disabled:opacity-50 bg-purple-700 hover:bg-purple-800"
            >
              {submitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>{submitProgress || 'Renewing…'}</span>
                </>
              ) : (
                <>
                  <span>⚡</span>
                  <span>Renew All ({displayedRows.length} Form 11 Certificates)</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Row Details Modal (Points 13–16) */}
      {editingDetailsRow && (
        <Modal
          open={Boolean(editingDetailsRow)}
          onClose={() => setEditingDetailsRow(null)}
          title={`Edit Remarks & Observations: ${editingDetailsRow.certificate_number} (${editingDetailsRow.equipment_name})`}
          size="lg"
        >
          <div className="space-y-4 p-1">
            <div className="grid grid-cols-2 gap-3 bg-slate-50 p-3 rounded-lg border border-slate-200">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Stop Valve</label>
                <select
                  value={editingDetailsRow.fittings_stop_valve}
                  onChange={(e) => {
                    const val = e.target.value;
                    setEditingDetailsRow((r) => r ? { ...r, fittings_stop_valve: val } : null);
                    updateRow(editingDetailsRow.id, { fittings_stop_valve: val });
                  }}
                  className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs bg-white"
                >
                  {CONDITION_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Reducing Valve</label>
                <select
                  value={editingDetailsRow.fittings_reducing_valve}
                  onChange={(e) => {
                    const val = e.target.value;
                    setEditingDetailsRow((r) => r ? { ...r, fittings_reducing_valve: val } : null);
                    updateRow(editingDetailsRow.id, { fittings_reducing_valve: val });
                  }}
                  className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs bg-white"
                >
                  {CONDITION_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Repairs Required (Point 13a)</label>
                <input
                  type="text"
                  value={editingDetailsRow.repairs_required}
                  onChange={(e) => {
                    const val = e.target.value;
                    setEditingDetailsRow((r) => r ? { ...r, repairs_required: val } : null);
                    updateRow(editingDetailsRow.id, { repairs_required: val });
                  }}
                  className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs bg-white"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Period for Repairs (Point 13b)</label>
                <input
                  type="text"
                  value={editingDetailsRow.repairs_period}
                  onChange={(e) => {
                    const val = e.target.value;
                    setEditingDetailsRow((r) => r ? { ...r, repairs_period: val } : null);
                    updateRow(editingDetailsRow.id, { repairs_period: val });
                  }}
                  className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs bg-white"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Other Conditions (Point 13c)</label>
                <input
                  type="text"
                  value={editingDetailsRow.repairs_other_condition}
                  onChange={(e) => {
                    const val = e.target.value;
                    setEditingDetailsRow((r) => r ? { ...r, repairs_other_condition: val } : null);
                    updateRow(editingDetailsRow.id, { repairs_other_condition: val });
                  }}
                  className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs bg-white"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Reduced Working Pressure Pending Repairs (Point 14)</label>
              <input
                type="text"
                value={editingDetailsRow.reduced_working_pressure_pending_repairs}
                onChange={(e) => {
                  const val = e.target.value;
                  setEditingDetailsRow((r) => r ? { ...r, reduced_working_pressure_pending_repairs: val } : null);
                  updateRow(editingDetailsRow.id, { reduced_working_pressure_pending_repairs: val });
                }}
                className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs bg-white"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Safe Working Pressure - Sub-rule 8 (Point 15)</label>
              <input
                type="text"
                value={editingDetailsRow.safe_working_pressure_subrule_8}
                onChange={(e) => {
                  const val = e.target.value;
                  setEditingDetailsRow((r) => r ? { ...r, safe_working_pressure_subrule_8: val } : null);
                  updateRow(editingDetailsRow.id, { safe_working_pressure_subrule_8: val });
                }}
                className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs bg-white"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Other Observations (Point 16)</label>
              <input
                type="text"
                value={editingDetailsRow.other_observations}
                onChange={(e) => {
                  const val = e.target.value;
                  setEditingDetailsRow((r) => r ? { ...r, other_observations: val } : null);
                  updateRow(editingDetailsRow.id, { other_observations: val });
                }}
                className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs bg-white"
              />
            </div>

            <div className="flex justify-end pt-3 border-t border-slate-200">
              <button
                type="button"
                onClick={() => setEditingDetailsRow(null)}
                className="btn btn-primary text-xs h-8 px-4"
              >
                Done
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Row Live PDF Preview Modal */}
      {previewRow && (
        <Modal
          open={Boolean(previewRow)}
          onClose={() => setPreviewRow(null)}
          title={`Preview Renewed Form 11: ${previewRow.certificate_number} — ${previewRow.equipment_name}`}
          size="lg"
        >
          <div className="h-[75vh] flex flex-col">
            {previewPdfUrl ? (
              <iframe
                src={previewPdfUrl}
                className="w-full flex-1 rounded-lg border border-slate-300 shadow bg-white"
                title="Renewed Form 11 PDF Preview"
              />
            ) : (
              <div className="h-full flex items-center justify-center text-sm text-slate-500 flex-col gap-2">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
                Loading certificate document preview…
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* Inline New Personnel Modal */}
      {showNewPersonnelModal && (
        <Modal
          open={showNewPersonnelModal}
          onClose={() => setShowNewPersonnelModal(false)}
          title="Add New Competent Person"
          size="xl"
        >
          <InlinePersonnelForm
            initialRole="competent_person"
            onCreated={(pers: Personnel) => {
              setInspectors((prev) => [...prev, { id: pers.id, full_name: pers.full_name }]);
              setBatchPersonnelId(pers.id);
              setShowNewPersonnelModal(false);
            }}
            onCancel={() => setShowNewPersonnelModal(false)}
          />
        </Modal>
      )}
    </Modal>
  );
}

interface Form11GroupCardProps {
  group: Form11StagedGroup;
  inspectors: Array<{ id: string; full_name: string }>;
  onApplyGroup: (
    groupKey: string,
    values: {
      examination_date?: string;
      personnel_id?: string;
      conditionsGood?: boolean;
      repairsDefault?: boolean;
    }
  ) => void;
  onUpdateRow: (id: string, partial: Partial<StagedForm11Renewal>) => void;
  onRemoveRow: (id: string) => void;
  onOpenRowPreview: (row: StagedForm11Renewal) => void;
  onEditDetails: (row: StagedForm11Renewal) => void;
  onOpenNewPersonnel: () => void;
}

function Form11GroupCard({
  group,
  inspectors,
  onApplyGroup,
  onUpdateRow,
  onRemoveRow,
  onOpenRowPreview,
  onEditDetails,
  onOpenNewPersonnel,
}: Form11GroupCardProps) {
  const [groupDate, setGroupDate] = useState(new Date().toISOString().slice(0, 10));
  const [groupPersonnelId, setGroupPersonnelId] = useState('');

  const handleApply = () => {
    onApplyGroup(group.key, {
      examination_date: groupDate,
      personnel_id: groupPersonnelId || undefined,
    });
  };

  const handleApplyGood = () => {
    onApplyGroup(group.key, {
      conditionsGood: true,
    });
  };

  const handleApplyRepairs = () => {
    onApplyGroup(group.key, {
      repairsDefault: true,
    });
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-xs overflow-hidden transition-all">
      {/* Group Header */}
      <div className="p-3 bg-slate-50 border-b border-slate-200 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="w-8 h-8 rounded-lg bg-purple-100 text-purple-700 font-bold flex items-center justify-center text-sm shadow-xs shrink-0">
            🛢️
          </span>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-bold text-slate-900 text-sm">{group.company_name}</span>
              <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold bg-purple-50 text-purple-700 border border-purple-200">
                📅 Inspected: {toDDMMYYYY(group.last_examination_date)}
              </span>
              <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-slate-100 text-slate-700 border border-slate-200">
                {group.items.length} vessel{group.items.length === 1 ? '' : 's'}
              </span>
            </div>
            <p className="text-[11px] text-slate-500 mt-0.5">
              Inspection batch from {toDDMMYYYY(group.last_examination_date)}. Mass update below or edit individual items.
            </p>
          </div>
        </div>

        {/* Group Mass Apply Bar */}
        <div className="flex flex-wrap items-center gap-2 text-xs bg-white p-1.5 rounded-lg border border-slate-200 shadow-xs">
          <span className="font-semibold text-slate-700 pl-1">⚡ Group Apply:</span>

          <div className="flex items-center gap-1">
            <span className="text-[11px] text-slate-500">Date:</span>
            <input
              type="date"
              value={groupDate}
              onChange={(e) => setGroupDate(e.target.value)}
              className="px-2 py-1 text-xs border border-slate-200 rounded font-medium bg-slate-50"
              title="Group New Examination Date"
            />
          </div>

          <div className="flex items-center gap-1">
            <span className="text-[11px] text-slate-500">Inspector:</span>
            <select
              value={groupPersonnelId}
              onChange={(e) => setGroupPersonnelId(e.target.value)}
              className="px-2 py-1 text-xs border border-slate-200 rounded font-medium bg-slate-50 max-w-[170px]"
            >
              <option value="">-- Choose Inspector --</option>
              {inspectors.map((ins) => (
                <option key={ins.id} value={ins.id}>{ins.full_name}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={onOpenNewPersonnel}
              title="Add New Personnel"
              className="px-1.5 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded text-xs cursor-pointer"
            >
              +
            </button>
          </div>

          <button
            type="button"
            onClick={handleApply}
            className="px-2.5 py-1 bg-purple-700 hover:bg-purple-800 text-white font-semibold rounded text-xs shadow-xs transition-colors cursor-pointer whitespace-nowrap"
          >
            Apply to Group
          </button>

          <button
            type="button"
            onClick={handleApplyGood}
            title="Set all fittings & vessels in this group to 'Good'"
            className="px-2 py-1 bg-purple-50 hover:bg-purple-100 text-purple-800 border border-purple-200 font-medium rounded text-[11px] transition-colors cursor-pointer whitespace-nowrap"
          >
            Set Group "Good"
          </button>

          <button
            type="button"
            onClick={handleApplyRepairs}
            title="Reset Points 13-16 in this group to standard defaults"
            className="px-2 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200 font-medium rounded text-[11px] transition-colors cursor-pointer whitespace-nowrap"
          >
            Reset Group P13-16
          </button>
        </div>
      </div>

      {/* Items Table in Group */}
      <div className="overflow-x-auto">
        <table className="w-full text-left text-xs border-collapse">
          <thead className="bg-slate-100/70 border-b border-slate-200 text-slate-600 font-semibold uppercase tracking-wider text-[11px]">
            <tr>
              <th className="py-2 px-3 w-10 text-center">#</th>
              <th className="py-2 px-3 min-w-[170px]">REPORT / CERT NO. & Vessel</th>
              <th className="py-2 px-3 min-w-[125px]">New Exam Date *</th>
              <th className="py-2 px-3 min-w-[100px]">Next Due</th>
              <th className="py-2 px-3 min-w-[180px]">Competent Person *</th>
              <th className="py-2 px-3 min-w-[110px]">Vessel Cond.</th>
              <th className="py-2 px-3 min-w-[110px]">Piping Cond.</th>
              <th className="py-2 px-3 min-w-[110px]">Gauges</th>
              <th className="py-2 px-3 min-w-[110px]">Safety Valve</th>
              <th className="py-2 px-3 min-w-[90px] text-center">Points 13–16</th>
              <th className="py-2 px-2 w-12 text-center">Preview</th>
              <th className="py-2 px-2 w-10 text-center">Status</th>
              <th className="py-2 px-2 w-10 text-center">✕</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {group.items.map((row, idx) => {
              const isReady = Boolean(row.personnel_id && row.examination_date);
              return (
                <tr key={row.id} className={!isReady ? 'bg-amber-50/30' : 'hover:bg-slate-50/70 transition-colors'}>
                  <td className="py-2 px-3 text-center text-slate-400 font-mono text-[11px]">{idx + 1}</td>
                  <td className="py-2 px-3">
                    <span className="font-mono font-bold text-purple-900 block">{row.certificate_number}</span>
                    <span className="text-[11px] font-medium text-slate-800 block truncate max-w-[180px]" title={row.equipment_name}>
                      {row.equipment_name}
                    </span>
                    {row.internal_id_number && row.internal_id_number !== '—' && (
                      <span className="text-[10px] font-mono text-slate-500 bg-slate-100 px-1 py-0.5 rounded">
                        Serial: {row.internal_id_number}
                      </span>
                    )}
                  </td>
                  <td className="py-2 px-3">
                    <input
                      type="date"
                      value={row.examination_date}
                      onChange={(e) => onUpdateRow(row.id, { examination_date: e.target.value })}
                      className="w-full px-2 py-1 text-xs border border-slate-300 rounded font-medium bg-white"
                      required
                    />
                  </td>
                  <td className="py-2 px-3 text-slate-600 font-mono text-[11px] whitespace-nowrap">
                    {toDDMMYYYY(nextInspDate11(row.examination_date))}
                  </td>
                  <td className="py-2 px-3">
                    <select
                      value={row.personnel_id}
                      onChange={(e) => onUpdateRow(row.id, { personnel_id: e.target.value })}
                      className={`w-full px-2 py-1 text-xs border rounded font-medium bg-white ${
                        !row.personnel_id ? 'border-amber-400 bg-amber-50/50' : 'border-slate-300'
                      }`}
                      required
                    >
                      <option value="">-- Select Inspector --</option>
                      {inspectors.map((ins) => (
                        <option key={ins.id} value={ins.id}>{ins.full_name}</option>
                      ))}
                    </select>
                  </td>
                  <td className="py-2 px-3">
                    <select
                      value={row.condition_vessel}
                      onChange={(e) => onUpdateRow(row.id, { condition_vessel: e.target.value })}
                      className="w-full px-2 py-1 text-xs border border-slate-300 rounded font-medium bg-white"
                    >
                      {CONDITION_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </td>
                  <td className="py-2 px-3">
                    <select
                      value={row.condition_piping}
                      onChange={(e) => onUpdateRow(row.id, { condition_piping: e.target.value })}
                      className="w-full px-2 py-1 text-xs border border-slate-300 rounded font-medium bg-white"
                    >
                      {CONDITION_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </td>
                  <td className="py-2 px-3">
                    <select
                      value={row.fittings_pressure_gauges}
                      onChange={(e) => onUpdateRow(row.id, { fittings_pressure_gauges: e.target.value })}
                      className="w-full px-2 py-1 text-xs border border-slate-300 rounded font-medium bg-white"
                    >
                      {CONDITION_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </td>
                  <td className="py-2 px-3">
                    <select
                      value={row.fittings_safety_valve}
                      onChange={(e) => onUpdateRow(row.id, { fittings_safety_valve: e.target.value })}
                      className="w-full px-2 py-1 text-xs border border-slate-300 rounded font-medium bg-white"
                    >
                      {CONDITION_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </td>
                  <td className="py-2 px-3 text-center">
                    <button
                      type="button"
                      onClick={() => onEditDetails(row)}
                      className="px-2 py-1 rounded bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold text-[11px] border border-slate-300 transition-colors whitespace-nowrap inline-flex items-center gap-1 cursor-pointer"
                    >
                      <span>⚙️ Edit</span>
                    </button>
                  </td>
                  <td className="py-2 px-2 text-center">
                    <button
                      type="button"
                      onClick={() => onOpenRowPreview(row)}
                      title="Preview Renewed PDF"
                      className="p-1 rounded hover:bg-purple-50 text-purple-700 transition-colors cursor-pointer"
                    >
                      👁️
                    </button>
                  </td>
                  <td className="py-2 px-2 text-center">
                    {isReady ? (
                      <span title="Ready to renew" className="text-emerald-600 font-bold text-sm">✓</span>
                    ) : (
                      <span title="Missing Inspector / Date" className="text-amber-500 font-bold text-sm">⚠️</span>
                    )}
                  </td>
                  <td className="py-2 px-2 text-center">
                    <button
                      type="button"
                      onClick={() => onRemoveRow(row.id)}
                      title="Exclude from batch"
                      className="p-1 rounded hover:bg-red-50 text-slate-400 hover:text-red-600 font-bold transition-colors cursor-pointer"
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
